import React from 'react';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { TLoginForm, TLoginFormValues } from './LoginForm.types';
import { useForm, Controller } from 'react-hook-form';
import { loginSchema } from './LoginForm.schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { LOGIN_FORM_DEFAULT_VALUES } from './LoginForm.constants';
import { Button, Link, TextInput } from 'components';
import './LoginForm.styled.scss';
import { useAppDispatch, useAppSelector } from 'store/store';
import {
  googleAuthAsyncThunk,
  loginUserAsyncThunk,
} from 'store/users/users.action';
import { ModalLayout } from '../../molecules/ModalLayout/ModalLayout';
import { pushNotification } from 'store/notifications/notifications.slices';
import { NOTIFICATION_TYPES } from 'store/notifications/notifications.types';

import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';
import { useNavigate } from 'react-router';
import { APP_ROUTES } from 'constants/routes';

export const LoginForm: TLoginForm = ({ closeModal }) => {
  const dispatch = useAppDispatch();
  const localize = useLocalize();
  const navigate = useNavigate();

  const { isLoading } = useAppSelector((state) => state.users.loginUser);

  const { control, handleSubmit } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: LOGIN_FORM_DEFAULT_VALUES,
    resolver: yupResolver(loginSchema),
  });

  const onSubmit = (data: TLoginFormValues) => {
    dispatch(
      loginUserAsyncThunk({
        body: data,
        onSuccess: closeModal,
      })
    );
  };

  const onGoogleSuccess = (credentialResponse: CredentialResponse) => {
    dispatch(
      googleAuthAsyncThunk({
        body: { idToken: credentialResponse.credential },
        onSuccess: closeModal,
      })
    );
  };

  const onForgotPassword = () => {
    closeModal();
    navigate(APP_ROUTES.REQUEST_RESET_PASSWORD);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="login-form__form"
      noValidate
    >
      <ModalLayout
        mainContent={
          <>
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label="Email"
                  placeholder="Enter your email"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
            <Controller
              control={control}
              name="password"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
            <div className="login-form__forgot-password">
              <Link onClick={onForgotPassword}>
                {localize('signinform.link.forgotpassword')}
              </Link>
            </div>

            <GoogleLogin
              text="signin"
              theme="filled_black"
              shape="circle"
              onSuccess={onGoogleSuccess}
              onError={() => {
                dispatch(
                  pushNotification({
                    message: 'Login Failed',
                    type: NOTIFICATION_TYPES.ERROR,
                  })
                );
              }}
            />
          </>
        }
        footerContent={
          <div className="login-form__actions">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading}
            >
              Login
            </Button>
          </div>
        }
      />
    </form>
  );
};
